import { Field, Form } from 'mobx-react-form';
import { toJS } from 'mobx';

import { isPlainObject, get, set, isNil, isEmpty, isEqual, isObject, isArray } from 'lodash';
import { FormField } from './types';

// Helper function to build all possible field paths
export const buildFieldPaths = (fields: FormField[], parentPath = ''): string[] => {
  return fields.reduce<string[]>((paths, field) => {
    const currentPath = parentPath ? `${parentPath}.${field.name}` : field.name;

    // Skip array subfield patterns (like salesHistory.[].id)
    if (field.name.includes('[]')) {
      return paths;
    }

    if (field.type === 'array') {
      return [...paths, currentPath];
    }

    if ('fields' in field && field.fields) {
      return [...paths, ...buildFieldPaths(field.fields, currentPath)];
    }

    return [...paths, currentPath];
  }, []);
};

export const getObjectPaths = (obj: any = {}, path = ''): string[] =>
  Object.entries(obj).flatMap(([key, value]) => {
    const currentPath = path ? `${path}.${key}` : key;
    // Don't traverse into arrays, just return their container path
    if (Array.isArray(value)) {
      return currentPath;
    }
    return isPlainObject(value) ? getObjectPaths(value, currentPath) : currentPath;
  });

export const setFormInitialValues = (form: Form, initialData: any = {}) => {
  getObjectPaths(initialData).forEach((path) => {
    try {
      const field: Field = form.select(path);
      if (!field) return;

      const storedValue = toJS(get(initialData, path));

      if (Array.isArray(storedValue) && field.type !== 'array') return;

      if (field.type === 'array') {
        /* 
          If the field value is the same as the stored value, we don't need to do anything
        */
        const isSame = areValuesEqual(field.value, storedValue);
        if (isSame) {
          return;
        }

        /*
         * If the initial value exists, we need to set the field value with the initial stored value
         */
        if (!isNil(storedValue)) {
          field.set([]);
          field.add(storedValue);
          field.initial = storedValue;
        }

        return;
      } else {
        field.set(storedValue);
        field.initial = storedValue;
      }
    } catch (error) {
      // console.warn(`Failed to set field at path: ${path}`, error);
    }
  });
};

export const getChangedFormValues = (form: Form, fields: FormField[], initialData: Record<string, any> = {}) => {
  const formData: Record<string, any> = {};

  buildFieldPaths(fields).forEach((path) => {
    try {
      const field: Field = form.select(path);
      if (!field) return;

      const initialValue = get(initialData, path);
      const currentValue = field.value;

      // eslint-disable-next-line no-restricted-syntax
      if (hasFieldValueChanged(field, initialValue)) {
        set(formData, path, currentValue);
      }
    } catch (error) {
      console.warn('Invalid path:', path, error);
    }
  });

  return formData;
};

export const removeArrayItemByIndex = (arrayField: Field, removedIndex: number) => {
  const values = arrayField.value || [];
  const lastIndex = values.length - 1;

  const newValues = values.filter((value: Record<string, any>, index: number) => index !== removedIndex);

  // this is correct, but consider using arrayField.clear() + arrayField.add()/arrayField.set() instead
  arrayField.set(newValues);
  arrayField.del(lastIndex.toString());
};

export const removeArrayItemByValue = (form: Form, arrayField: Field, dataPath: string, value: any) => {
  //eslint-disable-next-line no-restricted-syntax
  let removedItemPath: string | null | undefined = null;
  arrayField.each((field: Field) => {
    if (field.value[dataPath] === value) {
      removedItemPath = field.path;
    }
  });
  if (removedItemPath) {
    form.del(removedItemPath);
  }
};

const isEmptyValue = (value: any) => {
  if (typeof value === 'object') {
    return isEmpty(value);
  }

  return !value;
};

const areValuesEqual = (current: any, initial: any) => {
  const currentPOJO = toJS(current);
  const initialPOJO = toJS(initial);
  // eslint-disable-next-line no-restricted-syntax
  if (isEmptyValue(currentPOJO) && isEmptyValue(initialPOJO)) {
    return true;
  }

  if (isArray(currentPOJO) && isArray(initialPOJO) && currentPOJO?.length === initialPOJO?.length) {
    let areAllNestedValuesEqual = true;
    currentPOJO?.forEach((item: any, index: number) => {
      areAllNestedValuesEqual = areAllNestedValuesEqual && areValuesEqual(item, initialPOJO[index]);
    });
    return areAllNestedValuesEqual;
  }

  if (isObject(currentPOJO) && isObject(initialPOJO)) {
    let areAllNestedValuesEqual = true;
    // check if all properties of current object has the same value as in initial object
    // and if all properties of initial object are present in current object
    Object.getOwnPropertyNames(currentPOJO).forEach((key) => {
      areAllNestedValuesEqual =
        areAllNestedValuesEqual &&
        areValuesEqual(currentPOJO[key as keyof typeof currentPOJO], initialPOJO[key as keyof typeof initialPOJO]);
    });
    // check if all properties of initial object are present in current object
    Object.getOwnPropertyNames(initialPOJO).forEach((key) => {
      areAllNestedValuesEqual = areAllNestedValuesEqual && currentPOJO.hasOwnProperty(key);
    });
    return areAllNestedValuesEqual;
  }

  return isEqual(currentPOJO, initialPOJO);
};

const hasFieldValueChanged = (field: Field, initialValue: any) => {
  return !areValuesEqual(field.value, initialValue);
};
